import type { ChangeEvent } from 'react';
import { useCallback, useEffect, useMemo, useState, type MouseEventHandler } from 'react';
import { useTheme } from 'styled-components';

import {
  Alert,
  AllocationValueTypeEnum,
  Button,
  ButtonVariants,
  Dialog,
  Flex,
  FormGroup,
  HStack,
  IconName,
  Input,
  MixpanelEvent,
  PathTitle,
  Text,
  useMixpanel,
  type Allocation,
  type DialogProps,
  type UseDisclosureReturn,
} from '@talos/kyoko';

import { OMSSubAccounts, type OMSSubAccountsProps } from '@src/components/OMS/OMSSubAccounts';
import { useSubAccounts } from '@src/providers';

import { TradingSettingsContext, useTradingSettings } from '@src/providers/TradingSettingsContext';
import { DefaultTradingSettingsMethods } from '@src/providers/TradingSettingsContext.types';

interface TabSettingsDialogProps extends Omit<DialogProps, 'ref'>, UseDisclosureReturn {
  label: string;
  defaults: Record<string, any>;
  /** User can delete Market tab only if there is more than 1 Market tab type */
  canDeleteTab: boolean;
  onDelete: MouseEventHandler<HTMLButtonElement>;
  onConfirm: ({ label, defaults }: { label: string; defaults: Record<string, any> }) => void;
}

export const TabSettingsDialog = function TabSettingsDialog({
  onConfirm,
  onCancel,
  onDelete,
  label,
  defaults,
  canDeleteTab,
  ...props
}: TabSettingsDialogProps) {
  const [title, setTitle] = useState(label);
  const mixpanel = useMixpanel();
  const theme = useTheme();
  const [currentTabDefaults, setCurrentTabDefaults] = useState(defaults);
  const tradingSettings = useTradingSettings();
  const { tradableSubAccounts } = useSubAccounts();
  const [touched, setTouched] = useState<{
    [key: string]: boolean;
  }>({});
  const errors = {};
  useEffect(() => {
    setCurrentTabDefaults(defaults);
  }, [defaults]);

  const subAccountAllocations = useMemo<Allocation[]>((): Allocation[] => {
    const allocations = tradableSubAccounts
      ?.filter(sa => sa.SubaccountID === currentTabDefaults.defaultSubAccountId)
      ?.map(sa => ({
        subAccount: sa.Name,
        value: '',
      }));
    return allocations?.[0] ? allocations : [{ subAccount: '', value: '100' }];
  }, [currentTabDefaults, tradableSubAccounts]);

  const handleSubAccountUpdate: OMSSubAccountsProps['onUpdate'] = useCallback(
    changes => {
      if ('subAccountAllocations' in changes) {
        // NOTE: this will need a few updates yet before it works for allocations
        const firstAllocation = changes.subAccountAllocations[0];
        const defaultSubAccount = firstAllocation
          ? tradableSubAccounts?.find(sa => sa.Name === firstAllocation.subAccount)
          : null;
        setCurrentTabDefaults(prev => {
          const updatedDefaults = { ...prev };
          if (defaultSubAccount?.SubaccountID) {
            updatedDefaults.defaultSubAccountId = defaultSubAccount.SubaccountID;
          } else {
            delete updatedDefaults.defaultSubAccountId;
          }
          return updatedDefaults;
        });
        setTouched(prev => ({ ...prev, subAccountAllocations: true }));
        mixpanel.track(MixpanelEvent.SetDefaultSubAccount);
      }
    },
    [tradableSubAccounts, mixpanel]
  );

  return (
    <Dialog
      {...props}
      usePortal={false}
      headerIcon={IconName.Cog}
      title={<PathTitle paths={[label, 'Tab Settings']} />}
      customActions={
        <HStack
          justifyContent="flex-end"
          gap="spacing12"
          p="spacing16"
          w="100%"
          borderTop={`1px solid ${theme.backgroundDivider}`}
        >
          <Flex flex="1">
            <Button
              disabled={!canDeleteTab}
              startIcon={IconName.Trash}
              variant={ButtonVariants.Negative}
              onClick={onDelete}
            >
              Delete Tab
            </Button>
          </Flex>
          <Flex flex="1" justifyContent="space-between" gap="spacing8">
            <Button variant={ButtonVariants.Default} onClick={onCancel}>
              Cancel
            </Button>
            <Button
              variant={ButtonVariants.Primary}
              style={{ width: '100%' }}
              onClick={() =>
                onConfirm({
                  defaults: currentTabDefaults,
                  label: title,
                })
              }
            >
              Save
            </Button>
          </Flex>
        </HStack>
      }
    >
      <Alert dismissable={false} mb="spacing16">
        <Text color="colorTextDefault">
          Note that settings below are only applied to the <Text color="colorTextAttention">{label}</Text> tab.
        </Text>
      </Alert>
      {/* Override the trading settings methods to ensure that we can't set any global defaults*/}
      <TradingSettingsContext.Provider value={{ ...tradingSettings, ...DefaultTradingSettingsMethods }}>
        {/* Sub Accounts */}
        <OMSSubAccounts
          subAccountAllocations={subAccountAllocations}
          onUpdate={handleSubAccountUpdate}
          useAllocations={false}
          touched={touched}
          errors={errors}
          allocationValueType={AllocationValueTypeEnum.Percentage}
          quantityCurrency=""
          placeholder="<Use Global Default>"
          titlePrefix="Default "
          updateDefaultSubAccountOnChange={false}
        />
      </TradingSettingsContext.Provider>
      <FormGroup label="Tab label" error={!title ? 'Label is required' : ''}>
        <Input
          autoFocus // Avoid dropdown being focused and open when dialog opens
          invalid={!title}
          placeholder="Enter new label"
          value={title}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setTitle(e.target.value)}
        />
      </FormGroup>
    </Dialog>
  );
};
